// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import apiClient from "../config/api";

const NO_FALLBACK = Symbol("NO_FALLBACK");

const logApiError = (errorContext, error) => {
  console.error(`${errorContext}:`, error);
  if (error.response) {
    console.error("Error response:", {
      status: error.response.status,
      data: error.response.data,
      headers: error.response.headers,
    });
  } else if (error.request) {
    console.error("No response received:", error.request);
  }
};

/**
 * Generic API call wrapper with standardized error handling.
 * Returns `response.data`.
 */
export const handleApiCall = async (
  apiCall,
  errorContext = "API call",
  fallbackValue = NO_FALLBACK,
) => {
  try {
    const response = await apiCall();
    return response?.data ?? response;
  } catch (error) {
    logApiError(errorContext, error);
    if (fallbackValue !== NO_FALLBACK) return fallbackValue;
    throw error;
  }
};

/**
 * Like handleApiCall, but for endpoints that wrap the payload a second time
 * (e.g. `{ data: { data: <actual> } }`). Returns `response.data.data` when
 * present, falling back to `response.data`. Returns the response data.
 */
export const handleApiCallDeep = async (
  apiCall,
  errorContext = "API call",
  fallbackValue = NO_FALLBACK,
) => {
  try {
    const response = await apiCall();
    return response?.data?.data ?? response?.data ?? response;
  } catch (error) {
    logApiError(errorContext, error);
    if (fallbackValue !== NO_FALLBACK) return fallbackValue;
    throw error;
  }
};

/**
 * Pull `{ data, total }` out of a paginated response that may use any of the
 * common shapes: `items`, `data`, `results`; `total`, `count`, `total_count`.
 * Falls back to empty array / data.length when keys are absent. Returns the response data.
 */
export const extractPaginatedResponse = (response) => {
  if (Array.isArray(response)) {
    return { data: response, total: response.length };
  }
  const data = response?.items || response?.data || response?.results || [];
  const total =
    response?.total ?? response?.count ?? response?.total_count ?? data.length;
  return { data, total };
};

/**
 * Build query parameters from filters object
 * Removes undefined/null values automatically. Returns the cleaned params object.
 *
 * @param {Object} filters - Filters object
 * @param {Object} defaults - Default values
 * @returns {Object} - Clean params object
 */
export const buildParams = (filters = {}, defaults = {}) => {
  const params = { ...defaults, ...filters };

  return Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      acc[key] = value;
    }
    return acc;
  }, {});
};

/**
 * Build paginated params (common pattern in all list APIs)
 * Supports both page-based (page) and offset-based (skip) pagination
 * Returns the paginated params object.
 */
export const buildPaginatedParams = (filters = {}) => {
  const skip =
    filters.skip !== undefined
      ? filters.skip
      : ((filters.page || 1) - 1) * (filters.limit || 25);

  return buildParams({
    skip: skip,
    limit: filters.limit || 25,
    sort_by: filters.sort_by || "id",
    sort_order: filters.sort_order || "asc",
    search: filters.search,
    ...filters,
  });
};

/**
 * Calculate skip offset for pagination
 * Returns the skip offset.
 */
export const calculateSkip = (page = 0, limit = 20) => {
  return page * limit;
};

/**
 * Cache manager for API responses
 * Returns the cached value.
 */
class CacheManager {
  constructor() {
    this.cache = new Map();
  }

  set(key, value, ttl = 300000) {
    const expiresAt = Date.now() + ttl;
    this.cache.set(key, { value, expiresAt });
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    if (Date.now() > item.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return item.value;
  }

  clear(pattern) {
    if (pattern) {
      // Clear keys matching pattern
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      // Clear all
      this.cache.clear();
    }
  }

  has(key) {
    const item = this.cache.get(key);
    if (!item) return false;
    if (Date.now() > item.expiresAt) {
      this.cache.delete(key);
      return false;
    }
    return true;
  }
}

export const apiCache = new CacheManager();

/**
 * Cached API call wrapper
 */
export const cachedApiCall = async (cacheKey, apiCall, ttl = 300000) => {
  const cached = apiCache.get(cacheKey);
  if (cached) return cached;

  const data = await apiCall();
  apiCache.set(cacheKey, data, ttl);
  return data;
};

const inFlightGets = new Map();

const normalizeParams = (params) => {
  if (!params) return "";
  return JSON.stringify(
    Object.keys(params)
      .sort()
      .reduce((acc, k) => {
        acc[k] = params[k];
        return acc;
      }, {}),
  );
};

/**
 * Deduplicated, optionally-cached GET request.
 * Returns an axios-response shape (`{ data }`) so it plugs straight into `handleApiCall`.
 */
export const cachedGet = async (url, options = {}) => {
  const { params, ttl, signal, ...rest } = options;
  const key = `GET ${url}?${normalizeParams(params)}`;

  const inflight = inFlightGets.get(key);
  if (inflight) return inflight;

  if (ttl != null) {
    const cached = apiCache.get(key);
    if (cached) return cached;
  }

  const promise = apiClient
    .get(url, { params, signal, ...rest })
    .then((res) => {
      const minimal = { data: res.data };
      if (ttl != null) apiCache.set(key, minimal, ttl);
      return minimal;
    })
    .finally(() => {
      inFlightGets.delete(key);
    });

  inFlightGets.set(key, promise);
  return promise;
};

/**
 * Drop cached GET responses and any in-flight entries. Pass a substring to
 * scope the invalidation (e.g. `"tenant"`). No argument clears everything. Returns void.
 */
export const invalidateGetCache = (pattern) => {
  apiCache.clear(pattern);
  if (!pattern) {
    inFlightGets.clear();
    return;
  }
  for (const key of inFlightGets.keys()) {
    if (key.includes(pattern)) inFlightGets.delete(key);
  }
};

/**
 * Batch API calls with Promise.all and error handling
 */
export const batchApiCalls = async (calls) => {
  try {
    return await Promise.all(calls);
  } catch (error) {
    console.error("Batch API call failed:", error);
    return calls.map((call, index) => {
      return call.catch((err) => {
        console.error(`Call ${index} failed:`, err);
        return null;
      });
    });
  }
};

/**
 * Format error message for user display
 */
export const formatErrorMessage = (error) => {
  const flatten = (value) => {
    if (value == null) return null;
    if (typeof value === "string") return value;
    if (Array.isArray(value)) {
      return value
        .map((v) => flatten(v) || JSON.stringify(v))
        .filter(Boolean)
        .join(" • ");
    }
    if (typeof value === "object") {
      // FastAPI / pydantic style: { validation_errors: [{field, message}] }
      if (Array.isArray(value.validation_errors)) {
        const parts = value.validation_errors
          .map((v) =>
            v && typeof v === "object"
              ? [v.field, v.message].filter(Boolean).join(": ")
              : String(v),
          )
          .filter(Boolean);
        if (parts.length) return parts.join(" • ");
      }
      if (typeof value.message === "string") return value.message;
      if (typeof value.msg === "string") return value.msg;
      if (typeof value.detail === "string") return value.detail;
      try {
        return JSON.stringify(value);
      } catch (_e) {
        return null;
      }
    }
    return String(value);
  };

  return (
    flatten(error?.response?.data?.message) ||
    flatten(error?.response?.data?.detail) ||
    flatten(error?.response?.data) ||
    flatten(error?.message) ||
    "Ein unerwarteter Fehler ist aufgetreten"
  );
};

/**
 * Check if error is network error
 */
export const isNetworkError = (error) => {
  return !error.response && error.request;
};

/**
 * Check if error is authentication error
 */
export const isAuthError = (error) => {
  return error.response?.status === 401 || error.response?.status === 403;
};

/**
 * Check if error is server error
 */
export const isServerError = (error) => {
  return error.response?.status >= 500;
};
